/**
 * A short line, and an ⓘ that holds the rest of it.
 *
 * Owner-directed 2026-08-31: *"Too much copy on many screens. Don't put more than 5 words and put
 * lengthy content behind an (i) icon."* This is the one place that shape is built, so that a
 * screen adding an explanation gets the same affordance, the same panel, and the same escape
 * hatch rather than inventing a third one.
 *
 * ## Why the long text still exists
 *
 * Several of the sentences this component now hides are not decoration. The fee explanation is the second half of a choice the donor is being asked to
 * make; the deletion warning describes something irreversible. **Shortening the visible line is
 * not permission to drop the sentence** — it moves one tap away and stays reachable, which is why
 * this renders a real dialog rather than a tooltip that a touch device cannot open.
 *
 * The rule that follows from that: the visible label must be *true on its own*. "Cover the fee"
 * standing alone is true; "Cover the fee so 100% reaches them" needed the sentence after it to not
 * mislead, so that pair collapses to the short half, never the long one's first clause.
 */
import { useEffect, useId, useState, type ReactNode } from "react";
import { useDialogFocus } from "@givehere/ui";
import { InfoIcon } from "./icons.js";

export function Explain({
  label,
  title,
  children,
  className,
  tone,
}: {
  /** What stays on screen. Five words or fewer, and true without the panel. */
  label: ReactNode;
  /** Names the panel, and names the button for a screen reader. */
  title: string;
  /** The rest of it. */
  children: ReactNode;
  className?: string;
  tone?: "quiet" | "loud";
}) {
  const [open, setOpen] = useState(false);
  const id = useId();

  return (
    <>
      <p className={["explain", tone && `explain--${tone}`, className].filter(Boolean).join(" ")}>
        <span className="explain__label">{label}</span>{" "}
        <button
          type="button"
          className="explain__more"
          aria-label={title}
          aria-expanded={open}
          aria-controls={open ? id : undefined}
          onClick={() => setOpen(true)}
        >
          <InfoIcon />
        </button>
      </p>
      {open && (
        <ExplainPanel id={id} title={title} onDismiss={() => setOpen(false)}>
          {children}
        </ExplainPanel>
      )}
    </>
  );
}

/**
 * The ⓘ on its own, for a line whose short half is already a control.
 *
 * The fee row is the case this exists for: the visible five words are a *button* that takes the
 * offer, so wrapping them in `Explain`'s label would nest a button inside a paragraph that already
 * has one, and tapping the words would become ambiguous between "cover the fee" and "tell me
 * more". Here the words stay their own control and the ⓘ sits beside them.
 */
export function ExplainMore({
  title,
  children,
  className,
}: {
  title: string;
  children: ReactNode;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const id = useId();

  return (
    <>
      <button
        type="button"
        className={["explain__more", "explain__more--standalone", className]
          .filter(Boolean)
          .join(" ")}
        aria-label={title}
        aria-expanded={open}
        aria-controls={open ? id : undefined}
        onClick={() => setOpen(true)}
      >
        <InfoIcon />
      </button>
      {open && (
        <ExplainPanel id={id} title={title} onDismiss={() => setOpen(false)}>
          {children}
        </ExplainPanel>
      )}
    </>
  );
}

/**
 * The panel itself, separated so a screen that already has its own trigger — a footer button, a
 * row that is itself the affordance — can open one without rendering a second ⓘ beside it.
 */
export function ExplainPanel({
  id,
  title,
  children,
  onDismiss,
}: {
  id?: string;
  title: string;
  children: ReactNode;
  onDismiss: () => void;
}) {
  const panelRef = useDialogFocus<HTMLDivElement>();

  // Escape closes it, matching OrgInfoPanel. A panel reachable only by finding a small ✕ is a trap
  // on screens where the donor came looking for a reassurance, not a detour.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onDismiss();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onDismiss]);

  return (
    <div className="sheet" role="dialog" aria-modal="true" aria-label={title}>
      <div className="sheet__panel explain__panel" id={id} ref={panelRef} tabIndex={-1}>
        <button type="button" className="sheet__close" aria-label="Go back" onClick={onDismiss}>
          ✕
        </button>
        <h2 className="explain__title">{title}</h2>
        <div className="explain__body">{children}</div>
      </div>
    </div>
  );
}
